import { useEffect, useState } from 'react';
import { parseFilterString } from '../../../../../functions/utils/parseFilterString';
import { ComboInput } from '../../../baseInputs/ComboInput';
import { NumberInput } from '../../../baseInputs/NumberInput';

enum FilterType {
  Blur = 'blur',
  Brightness = 'brightness',
  Contrast = 'contrast',
  Grayscale = 'grayscale',
  HueRotate = 'hue-rotate',
  Invert = 'invert',
  Saturate = 'saturate',
  Sepia = 'sepia',
}

type FilterData = {
  filterType: FilterType;
  value: string | number;
};

export const FilterComboInput = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value?: string;
  onChange: (newValue?: string) => void;
}) => {
  const [filters, setFilters] = useState<FilterData[]>(
    value !== undefined && value !== null ? (parseFilterString(value) as FilterData[]) : []
  );

  const createFilterString = (filters?: { filterType: FilterType; value: string | number }[]) => {
    if (filters === undefined) return '';

    return filters.map((filter) => `${filter.filterType}(${filter.value})`).join(' ');
  };

  const updateFilter = (filterType: FilterType, value?: string | number) => {
    let updatedFilters = [...filters];

    if (value === undefined) {
      updatedFilters = filters.filter((filter) => filter.filterType !== filterType);
    } else {
      const filterID = updatedFilters.findIndex((filter) => filter.filterType === filterType);
      if (filterID !== -1) {
        updatedFilters[filterID].value = value;
      } else {
        updatedFilters.push({ filterType, value });
      }
    }

    setFilters([...updatedFilters]);
  };

  const getFilterValue = (filterType: FilterType) => {
    const filter = filters.find((filter) => filter.filterType === filterType);
    return filter?.value;
  };

  useEffect(() => {
    onChange(createFilterString(filters));
  }, [filters]);

  return (
    <ComboInput
      label={label}
      inputs={[
        {
          name: 'Blur',
          children: (
            <NumberInput
              label="blur"
              value={getFilterValue(FilterType.Blur)}
              onChange={(newValue) => updateFilter(FilterType.Blur, newValue)}
              defaultUnit="px"
              minValue={0}
              draggingSensitive={0.1}
              inputStyle={{ textAlign: 'center' }}
            />
          ),
          isVisible: filters.some((filter) => filter.filterType === FilterType.Blur),
          onAdd: () => updateFilter(FilterType.Blur, '5px'),
          onRemove: () => updateFilter(FilterType.Blur, undefined),
        },
        {
          name: 'Brightness',
          children: (
            <NumberInput
              label="brightness"
              value={getFilterValue(FilterType.Brightness)}
              onChange={(newValue) => updateFilter(FilterType.Brightness, newValue)}
              defaultUnit="%"
              percentMax={500}
              precision={2}
              minValue={0}
              draggingSensitive={0.1}
              draggingStep={10}
              inputStyle={{ textAlign: 'center' }}
            />
          ),
          isVisible: filters.some((filter) => filter.filterType === FilterType.Brightness),
          onAdd: () => updateFilter(FilterType.Brightness, '120%'),
          onRemove: () => updateFilter(FilterType.Brightness, undefined),
        },
        {
          name: 'Contrast',
          children: (
            <NumberInput
              label="contrast"
              value={getFilterValue(FilterType.Contrast)}
              onChange={(newValue) => updateFilter(FilterType.Contrast, newValue)}
              defaultUnit="%"
              percentMax={500}
              precision={0}
              minValue={0}
              draggingSensitive={0.1}
              draggingStep={10}
              inputStyle={{ textAlign: 'center' }}
            />
          ),
          isVisible: filters.some((filter) => filter.filterType === FilterType.Contrast),
          onAdd: () => updateFilter(FilterType.Contrast, '120%'),
          onRemove: () => updateFilter(FilterType.Contrast, undefined),
        },
        {
          name: 'Grayscale',
          children: (
            <NumberInput
              label="grayscale"
              value={getFilterValue(FilterType.Grayscale)}
              onChange={(newValue) => updateFilter(FilterType.Grayscale, newValue)}
              defaultUnit="%"
              draggingSensitive={0.1}
              draggingStep={10}
              inputStyle={{ textAlign: 'center' }}
            />
          ),
          isVisible: filters.some((filter) => filter.filterType === FilterType.Grayscale),
          onAdd: () => updateFilter(FilterType.Grayscale, '100%'),
          onRemove: () => updateFilter(FilterType.Grayscale, undefined),
        },
        {
          name: 'Hue-rotate',
          children: (
            <NumberInput
              label="hue-rotate"
              value={getFilterValue(FilterType.HueRotate)}
              onChange={(newValue) => updateFilter(FilterType.HueRotate, newValue)}
              defaultUnit="deg"
              precision={0}
              minValue={0}
              maxValue={360}
              draggingSensitive={0.1}
              draggingStep={10}
              inputStyle={{ textAlign: 'center' }}
            />
          ),
          isVisible: filters.some((filter) => filter.filterType === FilterType.HueRotate),
          onAdd: () => updateFilter(FilterType.HueRotate, '180deg'),
          onRemove: () => updateFilter(FilterType.HueRotate, undefined),
        },
        {
          name: 'Invert',
          children: (
            <NumberInput
              label="invert"
              value={getFilterValue(FilterType.Invert)}
              onChange={(newValue) => updateFilter(FilterType.Invert, newValue)}
              defaultUnit="%"
              draggingSensitive={0.1}
              draggingStep={10}
              inputStyle={{ textAlign: 'center' }}
            />
          ),
          isVisible: filters.some((filter) => filter.filterType === FilterType.Invert),
          onAdd: () => updateFilter(FilterType.Invert, '100%'),
          onRemove: () => updateFilter(FilterType.Invert, undefined),
        },
        {
          name: 'Saturate',
          children: (
            <NumberInput
              label="saturate"
              value={getFilterValue(FilterType.Saturate)}
              onChange={(newValue) => updateFilter(FilterType.Saturate, newValue)}
              defaultUnit="%"
              percentMax={500}
              draggingSensitive={0.1}
              draggingStep={10}
              inputStyle={{ textAlign: 'center' }}
            />
          ),
          isVisible: filters.some((filter) => filter.filterType === FilterType.Saturate),
          onAdd: () => updateFilter(FilterType.Saturate, '200%'),
          onRemove: () => updateFilter(FilterType.Saturate, undefined),
        },
        {
          name: 'Sepia',
          children: (
            <NumberInput
              label="sepia"
              value={getFilterValue(FilterType.Sepia)}
              onChange={(newValue) => updateFilter(FilterType.Sepia, newValue)}
              defaultUnit="%"
              draggingSensitive={0.1}
              draggingStep={10}
              inputStyle={{ textAlign: 'center' }}
            />
          ),
          isVisible: filters.some((filter) => filter.filterType === FilterType.Sepia),
          onAdd: () => updateFilter(FilterType.Sepia, '100%'),
          onRemove: () => updateFilter(FilterType.Sepia, undefined),
        },
      ]}
    />
  );
};
